/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Alert, App, Spin, Table } from 'antd'
import { DeleteOutlined, SendOutlined } from '@ant-design/icons'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Container } from 'src/components/Layout'
import PersonCell from 'src/components/List/PersonCell'
import { getTableStyles } from 'src/styles/tableStyles'
import useWindowSize from 'src/hooks/useWindowSize'
import Text from 'src/components/ts-components/Text'
import {
  fetchVideographers,
  removeVideographer,
  removeVideographerInvitation,
  resendVideographerInvitation,
  Videographer,
} from './api'
import DeleteVideographerModal from './DeleteVideographerModal'
import ResendVideographerModal from './ResendVideographerModal'

const PLACEHOLDER_IMAGE = '/images/generic_placeholders/placeholder_player.png'

const MAX_VIDEOGRAPHERS = 5

const EmptyVideographerState: React.FC = () => (
  <div
    css={css`
      border: 1px solid ${colors.SECONDARY_200};
      border-radius: 8px;
      overflow: hidden;
    `}
  >
    <div
      css={css`
        background-color: ${colors.SECONDARY_300};
        padding: 8px 16px;
        display: flex;
        align-items: center;
      `}
    >
      <span
        css={css`
          color: ${colors.NEUTRAL_70};
          font-size: 12px;
          text-transform: uppercase;
          font-weight: 500;
        `}
      >
        Name
      </span>
    </div>
    <div
      css={css`
        background-color: ${colors.SECONDARY_500};
        padding: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
      `}
    >
      <span
        css={css`
          color: ${colors.NEUTRAL_50};
          font-size: 14px;
        `}
      >
        No Videographer Assigned
      </span>
    </div>
  </div>
)

interface VideoAssignmentProps {
  gameId: string
  homeRosterId: string | null
  visitorRosterId: string | null
  homeTeamName: string
  visitorTeamName: string
  refreshKey?: number
  embedded?: boolean
}

const VideoAssignment: React.FC<VideoAssignmentProps> = ({
  gameId,
  homeRosterId,
  visitorRosterId,
  homeTeamName,
  visitorTeamName,
  refreshKey,
  embedded = false,
}) => {
  console.log('routes::: VideoAssignment')
  const { message } = App.useApp()
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [homeVideographers, setHomeVideographers] = useState<Videographer[]>([])
  const [visitorVideographers, setVisitorVideographers] = useState<
    Videographer[]
  >([])

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{
    videographer: Videographer
    teamType: 'home' | 'visitor'
  } | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Resend modal state
  const [resendModalOpen, setResendModalOpen] = useState(false)
  const [resendTarget, setResendTarget] = useState<{
    videographer: Videographer
    teamType: 'home' | 'visitor'
  } | null>(null)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)

  const loadVideographers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [homeData, visitorData] = await Promise.all([
        homeRosterId ? fetchVideographers(gameId, homeRosterId) : [],
        visitorRosterId ? fetchVideographers(gameId, visitorRosterId) : [],
      ])
      setHomeVideographers(homeData)
      setVisitorVideographers(visitorData)
    } catch (e) {
      const err = e as Error
      setError(err.message || 'Failed to load videographers')
    } finally {
      setLoading(false)
    }
  }, [gameId, homeRosterId, visitorRosterId])

  useEffect(() => {
    loadVideographers()
  }, [loadVideographers, refreshKey])

  const handleDeleteClick = useCallback(
    (videographer: Videographer, teamType: 'home' | 'visitor') => {
      setDeleteTarget({ videographer, teamType })
      setDeleteError(null)
      setDeleteModalOpen(true)
    },
    []
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) {
      return
    }

    const { videographer, teamType } = deleteTarget
    const rosterId = teamType === 'home' ? homeRosterId : visitorRosterId

    if (!rosterId) {
      return
    }

    setDeleteLoading(true)
    setDeleteError(null)

    try {
      if (videographer.user_exists && videographer.user_id) {
        await removeVideographer(gameId, rosterId, videographer.user_id)
      } else if (videographer.invitation_id) {
        await removeVideographerInvitation(
          gameId,
          rosterId,
          videographer.invitation_id
        )
      }

      // Refresh the list
      await loadVideographers()
      setDeleteModalOpen(false)
      setDeleteTarget(null)
      message.success('Videographer removed successfully')
    } catch (e) {
      const err = e as Error
      setDeleteError(err.message || 'Failed to remove videographer')
    } finally {
      setDeleteLoading(false)
    }
  }, [
    deleteTarget,
    gameId,
    homeRosterId,
    visitorRosterId,
    loadVideographers,
    message,
  ])

  const handleDeleteClose = useCallback(() => {
    if (!deleteLoading) {
      setDeleteModalOpen(false)
      setDeleteTarget(null)
      setDeleteError(null)
    }
  }, [deleteLoading])

  const handleResendClick = useCallback(
    (videographer: Videographer, teamType: 'home' | 'visitor') => {
      setResendTarget({ videographer, teamType })
      setResendError(null)
      setResendModalOpen(true)
    },
    []
  )

  const handleResendConfirm = useCallback(async () => {
    if (!resendTarget || !resendTarget.videographer.invitation_id) {
      return
    }

    setResendLoading(true)
    setResendError(null)

    try {
      await resendVideographerInvitation(
        resendTarget.videographer.invitation_id
      )
      setResendModalOpen(false)
      setResendTarget(null)
      message.success('Invitation resent successfully')
    } catch (e) {
      const err = e as Error
      setResendError(err.message || 'Failed to resend invitation')
    } finally {
      setResendLoading(false)
    }
  }, [resendTarget, message])

  const handleResendClose = useCallback(() => {
    if (!resendLoading) {
      setResendModalOpen(false)
      setResendTarget(null)
      setResendError(null)
    }
  }, [resendLoading])

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        key: 'name',
        dataIndex: 'name_first',
        render: (_: unknown, item: Videographer) => (
          <PersonCell
            item={{
              name_first:
                item.name_first || item.email?.split('@')[0] || 'Invited',
              name_last: item.name_last || '',
              image: item.avatar_url || PLACEHOLDER_IMAGE,
            }}
            subTitle={item.email || ''}
          />
        ),
      },
      {
        title: 'Status',
        key: 'status',
        width: 240,
        align: 'center' as const,
        render: (_: unknown, item: Videographer) => {
          const isReady = item.user_exists
          return (
            <span
              css={css`
                display: inline-block;
                padding: 2px 8px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 500;
                background-color: ${isReady
                  ? 'rgba(2, 47, 4, 0.3)'
                  : 'rgba(58, 41, 5, 0.3)'};
                border: 1px solid
                  ${isReady ? colors.SUCCESS_200 : colors.WARNING_200};
                color: ${isReady ? colors.SUCCESS_200 : colors.WARNING_200};
              `}
            >
              {isReady ? 'Ready to Stream' : 'Invited'}
            </span>
          )
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_: unknown, item: Videographer, _index: number) => (
          <div
            css={css`
              display: flex;
              gap: 8px;
            `}
          >
            {/* Resend button - only for invited users */}
            {!item.user_exists && item.invitation_id && (
              <button
                css={css`
                  background: transparent;
                  border: none;
                  cursor: pointer;
                  padding: 8px;
                  color: ${colors.PRIMARY};
                  transition: opacity 0.2s;
                  &:hover {
                    opacity: 0.7;
                  }
                `}
                onClick={() =>
                  handleResendClick(
                    item,
                    homeVideographers.includes(item) ? 'home' : 'visitor'
                  )
                }
                title='Resend invitation'
              >
                <SendOutlined style={{ fontSize: 18 }} />
              </button>
            )}
            {/* Delete button */}
            <button
              css={css`
                background: transparent;
                border: none;
                cursor: pointer;
                padding: 8px;
                color: ${colors.ERROR_200};
                transition: opacity 0.2s;
                &:hover {
                  opacity: 0.7;
                }
              `}
              onClick={() =>
                handleDeleteClick(
                  item,
                  homeVideographers.includes(item) ? 'home' : 'visitor'
                )
              }
              title='Remove videographer'
            >
              <DeleteOutlined style={{ fontSize: 18 }} />
            </button>
          </div>
        ),
      },
    ],
    [handleDeleteClick, handleResendClick, homeVideographers]
  )

  if (error && !loading) {
    return (
      // @ts-expect-error Container is a JavaScript component without TypeScript types
      <Container
        noWrap
        column
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 100%;
          margin: 0 8px;
        `}
      >
        <Alert message='Error' description={error} type='error' showIcon />
      </Container>
    )
  }

  return (
    // @ts-expect-error Container is a JavaScript component without TypeScript types
    <Container
      noWrap
      column
      css={css`
        ${!embedded && 'min-height: 100vh;'}
        width: 100%;
        margin: ${embedded ? '0' : '0 8px 24px 8px'};
        padding: ${embedded ? '0' : 'inherit'};
        .list-container {
          flex: 1;
        }

        .list-inner {
          margin-left: 0;
          margin-right: 0;
        }
      `}
    >
      {/* Home Team Section */}
      <Text
        variant='captionSmall'
        weight='semiBold'
        color={colors.NEUTRAL_50}
        style={`
          letter-spacing: 1.68px;
          text-transform: uppercase;
          margin-top: 0;
          margin-bottom: 16px;
          font-feature-settings: 'cv01' on, 'cv03' on, 'cv04' on;
        `}
      >
        {homeTeamName || 'Home Team'} ({homeVideographers.length}/
        {MAX_VIDEOGRAPHERS})
      </Text>

      {loading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100px;
          `}
        >
          <Spin
            size='small'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      ) : homeRosterId ? (
        homeVideographers.length > 0 ? (
          <Table
            columns={columns}
            dataSource={homeVideographers}
            rowKey={(record) => record.id || record.invitation_id || ''}
            pagination={false}
            size='small'
            scroll={{ x: isMobile ? 600 : undefined }}
            css={getTableStyles(isMobile)}
          />
        ) : (
          <EmptyVideographerState />
        )
      ) : (
        <Alert
          message='No Roster Available'
          description='The Home Team Roster Has Not Been Set Up Yet.'
          type='info'
          showIcon
          css={css`
            margin-bottom: 16px;
          `}
        />
      )}

      {/* Divider */}
      <div
        css={css`
          margin-top: 16px;
          margin-bottom: 16px;
          border-top: 1px solid ${colors.SECONDARY_200};
        `}
      />

      {/* Visitor Team Section */}
      <Text
        variant='captionSmall'
        weight='semiBold'
        color={colors.NEUTRAL_50}
        style={`
          letter-spacing: 1.68px;
          text-transform: uppercase;
          margin-top: 16px;
          margin-bottom: 16px;
          font-feature-settings: 'cv01' on, 'cv03' on, 'cv04' on;
        `}
      >
        {visitorTeamName || 'Visitor Team'} ({visitorVideographers.length}/
        {MAX_VIDEOGRAPHERS})
      </Text>

      {loading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100px;
          `}
        >
          <Spin
            size='small'
            css={css`
              .ant-spin-dot-item {
                background-color: ${colors.PRIMARY};
              }
            `}
          />
        </div>
      ) : visitorRosterId ? (
        visitorVideographers.length > 0 ? (
          <Table
            columns={columns}
            dataSource={visitorVideographers}
            rowKey={(record) => record.id || record.invitation_id || ''}
            pagination={false}
            size='small'
            scroll={{ x: isMobile ? 600 : undefined }}
            css={getTableStyles(isMobile)}
          />
        ) : (
          <EmptyVideographerState />
        )
      ) : (
        <Alert
          message='No Roster Available'
          description='The Visitor Team Roster Has Not Been Set Up Yet.'
          type='info'
          showIcon
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteVideographerModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        videographerName={
          deleteTarget
            ? `${deleteTarget.videographer.name_first} ${deleteTarget.videographer.name_last}`
            : ''
        }
        isLoading={deleteLoading}
        error={deleteError}
        teamName={
          deleteTarget?.teamType === 'home' ? homeTeamName : visitorTeamName
        }
      />

      {/* Resend Invitation Modal */}
      <ResendVideographerModal
        isOpen={resendModalOpen}
        onClose={handleResendClose}
        onConfirm={handleResendConfirm}
        videographerName={
          resendTarget
            ? `${resendTarget.videographer.name_first} ${resendTarget.videographer.name_last}`
            : ''
        }
        isLoading={resendLoading}
        error={resendError}
        teamName={
          resendTarget?.teamType === 'home' ? homeTeamName : visitorTeamName
        }
      />
    </Container>
  )
}

export default VideoAssignment
